/** Output size for profile avatars */
const OUTPUT_SIZE = 256;
/** JPEG quality (0-1) */
const QUALITY = 0.8;

export interface ProcessedImage {
  base64: string;
  width: number;
  height: number;
  sizeKB: number;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Process a profile image with explicit crop area and rotation.
 * - Crops to the user-selected region
 * - Resizes to OUTPUT_SIZE × OUTPUT_SIZE
 * - Rotates by the given degrees
 * - Compresses to JPEG
 */
export async function processProfileImage(
  imageSrc: string,
  cropArea: CropArea,
  rotation: number = 0,
): Promise<ProcessedImage> {
  const image = await loadImageFromSrc(imageSrc);

  // Source canvas: draw rotated full image
  const rotRad = (rotation * Math.PI) / 180;
  const { width: bw, height: bh } = getRotatedSize(image.width, image.height, rotRad);
  const srcCanvas = new OffscreenCanvas(bw, bh);
  const srcCtx = srcCanvas.getContext('2d')!;
  srcCtx.translate(bw / 2, bh / 2);
  srcCtx.rotate(rotRad);
  srcCtx.translate(-image.width / 2, -image.height / 2);
  srcCtx.drawImage(image, 0, 0);

  // Output canvas: crop and resize
  const outCanvas = new OffscreenCanvas(OUTPUT_SIZE, OUTPUT_SIZE);
  const outCtx = outCanvas.getContext('2d')!;
  outCtx.drawImage(
    srcCanvas,
    cropArea.x, cropArea.y, cropArea.width, cropArea.height,
    0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
  );

  const blob = await outCanvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY });
  const base64 = await blobToDataURL(blob);

  return { base64, width: OUTPUT_SIZE, height: OUTPUT_SIZE, sizeKB: Math.round(blob.size / 1024) };
}

function getRotatedSize(w: number, h: number, rotation: number) {
  const cos = Math.abs(Math.cos(rotation));
  const sin = Math.abs(Math.sin(rotation));
  return { width: Math.round(w * cos + h * sin), height: Math.round(w * sin + h * cos) };
}

function loadImageFromSrc(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

/** Convert a File to an object URL for use in the cropper */
export function fileToObjectURL(file: File): string {
  return URL.createObjectURL(file);
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to encode image'));
    reader.readAsDataURL(blob);
  });
}

export function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'PM';
}
